import isEqual from 'lodash/isEqual';
import { action, makeAutoObservable, observable, reaction, runInAction } from 'mobx';

import { logToNewRelic } from '../../services/debug';
import { getErrorMessage } from '../../utils/errors';

import { type AnimationKeys, MAX_ANIMATION_BLOCK_SCREEN_DURATION } from './settings/config';
import { type GlobalAnimationConfig, type AnimationHandlers } from './settings/types';

export class AnimationStore {
  @observable
  animations: Map<AnimationKeys, HTMLImageElement[]> = new Map();

  @observable
  currentFrames: Map<AnimationKeys, number> = new Map();

  @observable
  loadedCounts: Map<AnimationKeys, number> = new Map();

  @observable
  isTimeouts: Map<AnimationKeys, boolean> = new Map();

  @observable
  errors: Map<AnimationKeys, string> = new Map();

  @observable
  configs: Map<AnimationKeys, GlobalAnimationConfig> = new Map();

  @observable
  public isLoading: Map<AnimationKeys, boolean> = new Map();

  @observable
  public isAnimationRunning: Map<AnimationKeys, boolean> = new Map();

  @observable
  public isLoaded: Map<AnimationKeys, GlobalAnimationConfig | null> = new Map();

  @observable
  private runningStates: Map<AnimationKeys, boolean> = new Map();

  @observable
  public animationBlockScreenVisible: boolean = false;

  private animationBlockScreenTimeout: NodeJS.Timeout | null = null;

  constructor() {
    makeAutoObservable(this);
    reaction(() => this.animationBlockScreenVisible, this.onAnimationBlockScreenVisibilityChange);
  }

  updateAnimationConfig(key: AnimationKeys, animationConfig: GlobalAnimationConfig) {
    const currentConfig = this.configs.get(key);
    const config = { ...currentConfig, ...animationConfig };
    this.configs.set(key, config);
  }

  @action
  public setAnimationBlockScreenVisible(value: boolean) {
    this.animationBlockScreenVisible = value;
  }

  @action
  async loadImages(key: AnimationKeys) {
    const config = this.configs.get(key);

    if (!config || this.isLoading.get(key)) return;

    const hasErrors = this.hasError(key);
    const isLoaded = this.isLoaded.get(key);
    const isConfigEqual = isEqual(this.isLoaded.get(key), config);
    if (hasErrors || (isLoaded && isConfigEqual)) return;

    this.setIsLoading(key, true);

    this.animations.set(key, []);
    this.currentFrames.set(key, 0);
    this.loadedCounts.set(key, 0);
    this.isTimeouts.set(key, false);

    const images = this.animations.get(key)!;

    const startFrame = this.getStartFrame(config);
    const maxFrames = this.getMaxFrame(config);

    const loadSingleImage = (index: number): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const src = `${config.imagePath}${index.toString().padStart(config.digits, '0')}.${config.imageExt}`;
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = reject;
      });
    };

    try {
      const imagePromises = [];
      for (let i = startFrame; i <= maxFrames; i++) {
        imagePromises.push(loadSingleImage(i));
      }

      const loadedImages = await Promise.all(imagePromises);

      loadedImages.forEach((img, i) => {
        const index = i + startFrame;
        this.loadedCounts.set(key, (this.loadedCounts.get(key) || 0) + 1);
        images[index] = img;
      });

      this.setIsLoaded(key, config);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error(`Error loading images: ${errorMessage}`);
      this.setError(key, 'Error loading images');
      logToNewRelic(
        'AnimationStoreError',
        {
          error: `Error loading images: ${errorMessage}`,
          key,
          loaded: this.loadedCounts.get(key),
          maxFrames,
          startFrame,
        },
        'error',
      );
    } finally {
      this.setIsLoading(key, false);
    }
  }

  @action
  startAnimation(
    key: AnimationKeys,
    ctx: CanvasRenderingContext2D,
    handlers?: AnimationHandlers,
    defaultStartFrame?: number,
    defaultMaxFrames?: number,
  ) {
    const config = this.configs.get(key);
    const isAnimationRunning = this.checkAnimationRunning(key);
    if (!config || isAnimationRunning) {
      return;
    }

    this.setAnimationRunning(key, true);
    void handlers?.onAnimationStart?.(key);

    this.runningStates.set(key, true);

    const fps = 30;
    const frameDuration = 1000 / fps;
    let lastTime = performance.now();
    let lastFrameUpdate = lastTime;

    const startFrame = defaultStartFrame ?? this.getStartFrame(config);
    const maxFrames = defaultMaxFrames ?? this.getMaxFrame(config);
    const totalFrames = maxFrames - startFrame;

    this.setCanvasSize(ctx, config);

    const loadingTimeoutId = setTimeout(() => {
      const loadedCount = this.loadedCounts.get(key) || 0;
      if (loadedCount < totalFrames) {
        this.isTimeouts.set(key, true);
        this.setAnimationRunning(key, false);
        handlers?.onAnimationError?.(key);
        handlers?.onTimeout?.(key);
        logToNewRelic(
          'AnimationStoreError',
          {
            error: 'Loading timeout',
            key,
            loaded: loadedCount,
            totalFrames,
            startFrame,
          },
          'error',
        );
      }
    }, config.loadingTimeoutMs || 4000);

    const renderTimeoutMs = config.renderTimeoutMs || 2000;
    const renderTimeoutId = setInterval(() => {
      const currentTime = performance.now();
      const currentFrame = this.currentFrames.get(key) || 0;
      if (currentTime - lastFrameUpdate > renderTimeoutMs) {
        this.isTimeouts.set(key, true);
        this.setAnimationRunning(key, false);
        clearTimeout(loadingTimeoutId);
        clearInterval(renderTimeoutId);
        handlers?.onAnimationError?.(key);
        logToNewRelic(
          'AnimationStoreError',
          {
            error: 'Rendering timeout',
            key,
            currentFrame,
            totalFrames,
            startFrame,
          },
          'error',
        );
      }
    }, renderTimeoutMs);

    const animate = (currentTime: number) => {
      if (!this.runningStates.get(key)) {
        clearTimeout(loadingTimeoutId);
        clearInterval(renderTimeoutId);
        this.setAnimationRunning(key, false);
        this.currentFrames.set(key, 0);
        return;
      }

      const elapsedTime = currentTime - lastTime;

      if (elapsedTime >= frameDuration) {
        lastTime = currentTime;
        const currentFrame = this.currentFrames.get(key) || 0;

        if (currentFrame <= totalFrames) {
          this.updateCanvas(ctx, key, currentFrame, startFrame, handlers);
          lastFrameUpdate = currentTime;
        } else {
          clearTimeout(loadingTimeoutId);
          clearInterval(renderTimeoutId);
          handlers?.onAnimationEnd?.(key);
          this.setAnimationRunning(key, false);
          if (config.loop) {
            this.handleLoop(key, ctx, handlers, startFrame, maxFrames);
          } else {
            this.currentFrames.set(key, 0);
          }
          return;
        }
      }

      requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }

  private updateCanvas(
    ctx: CanvasRenderingContext2D,
    key: AnimationKeys,
    currentFrame: number,
    startFrame: number,
    handlers?: AnimationHandlers,
  ) {
    const img = this.animations.get(key)![currentFrame + startFrame];
    if (img) {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.drawImage(img, 0, 0, ctx.canvas.width, ctx.canvas.height);
    }

    runInAction(() => {
      this.currentFrames.set(key, currentFrame + 1);
      handlers?.onFrameChange?.(key, currentFrame + startFrame);
    });
  }

  private setCanvasSize(ctx: CanvasRenderingContext2D, config: GlobalAnimationConfig) {
    if (ctx.canvas.width !== config.animationSize.width || ctx.canvas.height !== config.animationSize.height) {
      ctx.canvas.width = config.animationSize.width;
      ctx.canvas.height = config.animationSize.height;
    }
  }

  @action
  handleLoop(
    key: AnimationKeys,
    ctx: CanvasRenderingContext2D,
    handlers?: AnimationHandlers,
    defaultStartFrame?: number,
    defaultMaxFrames?: number,
  ) {
    const config = this.configs.get(key);
    if (!config) return;
    const startFrame = this.getStartFrame(config);
    this.currentFrames.set(key, startFrame);
    this.startAnimation(key, ctx, handlers, defaultStartFrame, defaultMaxFrames);
  }

  @action
  stopAnimation(key: AnimationKeys) {
    if (this.checkAnimationRunning(key)) {
      this.runningStates.set(key, false);
    }
  }

  getAnimationImages(key: AnimationKeys) {
    return this.animations.get(key) || [];
  }

  getCurrentFrame(key: AnimationKeys) {
    return this.currentFrames.get(key) || 0;
  }

  getStartFrame(config: GlobalAnimationConfig) {
    if (!config) {
      return 0;
    }

    return config.startFrame;
  }

  getMaxFrame(config: GlobalAnimationConfig) {
    if (!config) {
      return 0;
    }

    return config.maxFrames;
  }
  checkIsTimeout(key: AnimationKeys | null) {
    if (!key) return false;
    return this.isTimeouts.get(key) ?? false;
  }

  checkIsLoaded(key: AnimationKeys) {
    return this.isLoaded.get(key) ?? false;
  }

  @action
  setIsLoading(key: AnimationKeys, isLoading: boolean) {
    if (!key) return;
    this.isLoading.set(key, isLoading);
  }

  checkIsLoading(key: AnimationKeys | null) {
    if (!key) return false;
    return this.isLoading.get(key) ?? false;
  }

  getConfig(key: AnimationKeys | null) {
    if (!key) return null;
    return this.configs.get(key) || null;
  }

  setError(key: AnimationKeys, error: string) {
    this.errors.set(key, error);
  }

  hasError(key: AnimationKeys | null) {
    if (!key) return false;
    return this.errors.get(key) || false;
  }

  setAnimationRunning(key: AnimationKeys, isRunning: boolean) {
    this.isAnimationRunning.set(key, isRunning);
  }

  checkAnimationRunning(key: AnimationKeys | null) {
    if (!key) return false;
    return this.isAnimationRunning.get(key) || false;
  }

  setIsLoaded(key: AnimationKeys, isLoaded: GlobalAnimationConfig | null) {
    this.isLoaded.set(key, isLoaded);
  }

  private onAnimationBlockScreenVisibilityChange = (visible: boolean) => {
    if (visible) {
      if (this.animationBlockScreenTimeout) {
        clearTimeout(this.animationBlockScreenTimeout);
      }
      this.animationBlockScreenTimeout = setTimeout(() => {
        this.setAnimationBlockScreenVisible(false);
      }, MAX_ANIMATION_BLOCK_SCREEN_DURATION);
    } else {
      if (this.animationBlockScreenTimeout) {
        clearTimeout(this.animationBlockScreenTimeout);
      }
    }
  };
}
